/**
 * RxJS 기반 카메라 액션 레이어
 * 마우스 이벤트 스트림을 구독하여 카메라 상태(panOffset, zoom)를 업데이트
 */
import {
  Subject,
  BehaviorSubject,
  Observable,
  Subscription,
  merge,
} from "rxjs";
import {
  filter,
  withLatestFrom,
  debounceTime,
  takeUntil,
} from "rxjs/operators";
import * as THREE from "three";
import {
  MouseEventStreams,
  type EdgeZone,
  type MousePosition,
} from "./mouseEvents";
import { DEFAULT_PROJECTION_PARAMS } from "../config";

// ============================================================
// 상태 타입 정의
// ============================================================

export interface CameraState {
  panOffset: THREE.Vector3;
  zoom: number;
}

export interface CameraConfig {
  panSpeed: number;
  zoomSpeed: number;
  minZoom: number;
  maxZoom: number;
  edgeThresholdX: number;
  edgeThresholdY: number;
  edgePanSpeed: number;
}

const DEFAULT_CONFIG: CameraConfig = {
  panSpeed: 0.9,
  zoomSpeed: 0.05,
  minZoom: 0.01, // 최대 줌인
  maxZoom: 0.2, // 최대 줌아웃
  edgeThresholdX: 300,
  edgeThresholdY: 300,
  edgePanSpeed: 0.8,
};

// ============================================================
// 카메라 액션 클래스
// ============================================================

export class CameraActions {
  private mouseEvents: MouseEventStreams;
  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  // 상태 Subject들
  private panOffset$ = new BehaviorSubject<THREE.Vector3>(new THREE.Vector3());
  private zoom$ = new BehaviorSubject<number>(1.0);
  private edgeZone$ = new BehaviorSubject<EdgeZone>({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });
  private isDragging$ = new BehaviorSubject<boolean>(false);
  private lastMousePosition$ = new BehaviorSubject<MousePosition | null>(null);
  private edgeScrollEnabled$ = new BehaviorSubject<boolean>(true); // 가장자리 스크롤 활성화 여부

  // 설정
  private config: CameraConfig;

  // 카메라 참조 (줌 업데이트용)
  private camera: THREE.Camera | null = null;

  // 상태 변경 디바운스 콜백
  private onStateChange:
    | ((state: { x: number; y: number; zoom: number }) => void)
    | null = null;
  private stateChange$ = new Subject<{ x: number; y: number; zoom: number }>();

  constructor(
    mouseEvents: MouseEventStreams,
    config: Partial<CameraConfig> = {},
    camera?: THREE.Camera,
    onStateChange?: (state: { x: number; y: number; zoom: number }) => void
  ) {
    this.mouseEvents = mouseEvents;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.camera = camera || null;
    this.onStateChange = onStateChange || null;

    // 카메라 초기 줌 동기화
    if (camera instanceof THREE.OrthographicCamera) {
      this.zoom$.next(1 / camera.zoom);
    }

    this.setupDragPanning();
    this.setupWheelZoom();
    this.setupEdgeScrolling();
    this.setupStateChangeDebounce();
  }

  // ============================================================
  // 드래그 패닝 (우클릭 드래그)
  // ============================================================

  private setupDragPanning(): void {
    // 이전 마우스 위치 추적
    let prevMouseX = 0;
    let prevMouseY = 0;

    // 우클릭 다운 → 드래그 시작, 시작 위치 저장
    const dragStartSub = this.mouseEvents.rightMouseDown$
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => {
        this.isDragging$.next(true);
        prevMouseX = e.clientX;
        prevMouseY = e.clientY;
      });

    this.subscriptions.push(dragStartSub);

    // 드래그 종료 (마우스 업 또는 마우스 떠남)
    const dragEnd$ = merge(this.mouseEvents.up$, this.mouseEvents.leave$);

    const dragEndSub = dragEnd$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isDragging$.next(false);
    });

    this.subscriptions.push(dragEndSub);

    // 드래그 중 마우스 이동 → 패닝
    const moveSub = this.mouseEvents.move$
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.isDragging$, this.zoom$),
        filter(([_, isDragging]) => isDragging)
      )
      .subscribe(([event, _, zoom]) => {
        const deltaX = event.clientX - prevMouseX;
        const deltaY = event.clientY - prevMouseY;

        // 현재 위치를 이전 위치로 업데이트
        prevMouseX = event.clientX;
        prevMouseY = event.clientY;

        // 줌 레벨에 따른 패닝 속도 조정
        const adjustedSpeed = this.config.panSpeed * zoom;

        const currentOffset = this.panOffset$.value.clone();
        // 드래그 패닝: 마우스를 오른쪽으로 드래그하면 화면이 오른쪽으로 이동
        currentOffset.x += deltaX * adjustedSpeed;
        currentOffset.y -= deltaY * adjustedSpeed;

        this.panOffset$.next(currentOffset);
        this.emitStateChange();
      });

    this.subscriptions.push(moveSub);
  }

  // ============================================================
  // 휠 줌
  // ============================================================

  private setupWheelZoom(): void {
    const sub = this.mouseEvents.wheelEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => {
        const delta = e.deltaY * 0.001;
        const currentZoom = this.zoom$.value;
        const newZoom = THREE.MathUtils.clamp(
          currentZoom + delta * this.config.zoomSpeed,
          this.config.minZoom,
          this.config.maxZoom
        );

        this.zoom$.next(newZoom);

        // 카메라 줌 업데이트
        if (this.camera instanceof THREE.OrthographicCamera) {
          this.camera.zoom = 1 / newZoom;
          this.camera.updateProjectionMatrix();
        }

        // 가장자리 패닝 속도 업데이트
        this.config.edgePanSpeed = 0.5 * Math.sqrt(newZoom);

        this.emitStateChange();
      });

    this.subscriptions.push(sub);
  }

  // ============================================================
  // 가장자리 스크롤 (조이스틱 방식)
  // ============================================================

  private setupEdgeScrolling(): void {
    // 마우스 위치 추적
    this.mouseEvents.mousePosition$
      .pipe(takeUntil(this.destroy$))
      .subscribe((pos) => {
        this.lastMousePosition$.next(pos);
      });

    // 마우스 떠남 시 위치 초기화
    this.mouseEvents.leave$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.lastMousePosition$.next(null);
      this.edgeZone$.next({
        left: false,
        right: false,
        top: false,
        bottom: false,
      });
    });

    // 애니메이션 프레임마다 가장자리 체크 및 스크롤
    const sub = this.mouseEvents.animationFrame$
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.lastMousePosition$, this.isDragging$, this.zoom$)
      )
      .subscribe(([_, mousePos, isDragging, zoom]) => {
        // 드래그 중이거나 가장자리 스크롤이 비활성화되면 스크롤 안함
        if (isDragging || !this.edgeScrollEnabled$.value) {
          // 비활성화 상태일 때 빈 엣지존 반환
          if (!this.edgeScrollEnabled$.value) {
            this.edgeZone$.next({
              left: false,
              right: false,
              top: false,
              bottom: false,
            });
          }
          return;
        }

        if (!mousePos) {
          this.edgeZone$.next({
            left: false,
            right: false,
            top: false,
            bottom: false,
          });
          return;
        }

        const viewport = this.mouseEvents.getViewportInfo();
        const { width, height, centerX, centerY } = viewport;
        const { edgeThresholdX, edgeThresholdY, edgePanSpeed } = this.config;
        const { x, y } = mousePos;

        // 뷰포트 밖이면 무시
        if (x < 0 || y < 0 || x > width || y > height) {
          this.edgeZone$.next({
            left: false,
            right: false,
            top: false,
            bottom: false,
          });
          return;
        }

        // 가장자리 영역 감지
        const edgeZone: EdgeZone = {
          left: x < edgeThresholdX,
          right: x > width - edgeThresholdX,
          top: y < edgeThresholdY,
          bottom: y > height - edgeThresholdY,
        };

        const isInEdgeZone =
          edgeZone.left || edgeZone.right || edgeZone.top || edgeZone.bottom;

        this.edgeZone$.next(edgeZone);

        if (!isInEdgeZone) return;

        // 강도 계산
        let intensity = 0;
        if (edgeZone.left) {
          intensity = Math.max(
            intensity,
            (edgeThresholdX - x) / edgeThresholdX
          );
        }
        if (edgeZone.right) {
          intensity = Math.max(
            intensity,
            (x - (width - edgeThresholdX)) / edgeThresholdX
          );
        }
        if (edgeZone.top) {
          intensity = Math.max(
            intensity,
            (edgeThresholdY - y) / edgeThresholdY
          );
        }
        if (edgeZone.bottom) {
          intensity = Math.max(
            intensity,
            (y - (height - edgeThresholdY)) / edgeThresholdY
          );
        }

        // 방향 벡터 계산 (중심에서 마우스 방향)
        const dx = x - centerX;
        const dy = y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance === 0) return;

        const directionX = dx / distance;
        const directionY = dy / distance;

        // 패닝 적용
        // final 프로젝트와 동일한 방향 로직:
        // - 마우스가 오른쪽 가장자리에 있으면 → 씬이 왼쪽으로 이동 (panOffset.x 감소)
        // - 마우스가 아래쪽 가장자리에 있으면 → 씬이 위로 이동 (panOffset.y 증가)
        const speed = edgePanSpeed * intensity;
        const currentOffset = this.panOffset$.value.clone();

        // final과 동일한 방향 벡터 적용
        currentOffset.x -= directionX * speed;
        currentOffset.y += directionY * speed;

        this.panOffset$.next(currentOffset);
        this.emitStateChange();
      });

    this.subscriptions.push(sub);
  }

  // ============================================================
  // 상태 변경 디바운스 (500ms)
  // ============================================================

  private setupStateChangeDebounce(): void {
    const sub = this.stateChange$
      .pipe(takeUntil(this.destroy$), debounceTime(500))
      .subscribe((state) => {
        if (this.onStateChange) {
          this.onStateChange(state);
        }
      });

    this.subscriptions.push(sub);
  }

  private emitStateChange(): void {
    const panOffset = this.panOffset$.value;
    this.stateChange$.next({
      x: panOffset.x,
      y: panOffset.y,
      zoom: this.zoom$.value,
    });
  }

  // ============================================================
  // 공개 API
  // ============================================================

  /** 현재 panOffset 가져오기 */
  getPanOffset(): THREE.Vector3 {
    return this.panOffset$.value;
  }

  /** panOffset Observable */
  get panOffsetObservable$(): Observable<THREE.Vector3> {
    return this.panOffset$.asObservable();
  }

  /** 현재 zoom 가져오기 */
  getZoom(): number {
    return this.zoom$.value;
  }

  /** zoom Observable */
  get zoomObservable$(): Observable<number> {
    return this.zoom$.asObservable();
  }

  /** 현재 가장자리 영역 상태 */
  getEdgeZone(): EdgeZone {
    return this.edgeZone$.value;
  }

  /** 가장자리 영역 Observable */
  get edgeZoneObservable$(): Observable<EdgeZone> {
    return this.edgeZone$.asObservable();
  }

  /** 드래그 상태 Observable */
  get isDraggingObservable$(): Observable<boolean> {
    return this.isDragging$.asObservable();
  }

  /** 상태 설정 (복원용) */
  setState(x: number, y: number, zoom: number): void {
    this.panOffset$.next(new THREE.Vector3(x, y, 0));
    this.zoom$.next(zoom);

    if (this.camera instanceof THREE.OrthographicCamera) {
      this.camera.zoom = 1 / zoom;
      this.camera.updateProjectionMatrix();
    }
  }

  /** 카메라 초기화 (0,0, 줌 1.0) */
  resetCamera(): void {
    this.panToWorld(0, 0);
    this.zoom$.next(1.0);
  }

  /** 가장자리 스크롤 활성화/비활성화 설정 */
  setEdgeScrollEnabled(enabled: boolean): void {
    this.edgeScrollEnabled$.next(enabled);
    if (!enabled) {
      // 비활성화 시 즉시 엣지존 초기화
      this.edgeZone$.next({
        left: false,
        right: false,
        top: false,
        bottom: false,
      });
    }
  }

  /**
   * 월드 좌표로 카메라 이동 (해당 위치를 화면 중앙으로)
   * @param worldX 월드 X 좌표
   * @param worldZ 월드 Z 좌표 (Isometric에서는 Z가 깊이)
   */
  panToWorld(worldX: number, worldZ: number): void {
    // Oblique 투영 파라미터를 presets에서 가져옴 (projection.ts와 동일하게)
    const { thetaX, thetaY, thetaZ, scaleX, scaleY, scaleZ } = DEFAULT_PROJECTION_PARAMS;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const rx = toRad(thetaX);
    const ry = toRad(thetaY);
    const rz = toRad(thetaZ);
    // X, Y, Z축 벡터 (projection.ts와 동일)
    const xVec = { x: Math.cos(rx) * scaleX, y: Math.sin(rx) * scaleX };
    const yVec = { x: Math.cos(ry) * scaleY, y: Math.sin(ry) * scaleY };
    const zVec = { x: Math.cos(rz) * scaleZ, y: Math.sin(rz) * scaleZ };
    // 월드 (x, z) → 스크린 (x, y)
    // (Y축은 일반적으로 0이지만, Oblique 행렬과 일치시키기 위해 포함)
    // worldY는 0으로 가정
    const screenX = worldX * xVec.x + /* worldY * yVec.x + */ worldZ * zVec.x;
    const screenY = worldX * xVec.y + /* worldY * yVec.y + */ worldZ * zVec.y;
    // 해당 월드 좌표가 화면 중앙에 오도록 panOffset 설정
    this.setState(-screenX, -screenY, this.zoom$.value);
  }

  /** 카메라 설정 */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
    if (camera instanceof THREE.OrthographicCamera) {
      this.zoom$.next(1 / camera.zoom);
    }
  }

  /** 카메라 가져오기 */
  getCamera(): THREE.Camera | null {
    return this.camera;
  }

  // 저장된 카메라 상태 (줌 아웃 복원용)
  private savedState: { x: number; y: number; zoom: number } | null = null;

  /** 현재 카메라 상태 저장 */
  saveState(): void {
    const panOffset = this.panOffset$.value;
    this.savedState = {
      x: panOffset.x,
      y: panOffset.y,
      zoom: this.zoom$.value,
    };
  }

  /** 저장된 상태로 애니메이션 복원 */
  async restoreState(duration: number = 0.5): Promise<void> {
    if (!this.savedState) return;

    const { x, y, zoom } = this.savedState;
    await this.animateTo(x, y, zoom, duration);
    this.savedState = null;
  }

  /** 저장된 상태가 있는지 확인 */
  hasSavedState(): boolean {
    return this.savedState !== null;
  }

  /**
   * 월드 좌표로 애니메이션 이동 (GSAP 사용)
   * @param worldX 월드 X 좌표
   * @param worldZ 월드 Z 좌표
   * @param targetZoom 목표 줌 레벨
   * @param duration 애니메이션 시간 (초)
   */
  async animatedPanToWorld(
    worldX: number,
    worldZ: number,
    targetZoom: number,
    duration: number = 0.5
  ): Promise<void> {
    // Isometric 투영 각도 (DEFAULT_PROJECTION_PARAMS와 동일)
    const thetaX = 210; // degrees
    const thetaZ = 330; // degrees
    const toRad = (deg: number) => (deg * Math.PI) / 180;

    // 월드 X, Z를 스크린 좌표로 투영
    const screenX =
      worldX * Math.cos(toRad(thetaX)) + worldZ * Math.cos(toRad(thetaZ));
    const screenY =
      worldX * -Math.sin(toRad(thetaX)) + worldZ * -Math.sin(toRad(thetaZ));

    // Pan 오프셋을 반전시켜 해당 위치가 중앙에 오도록
    // 줌 값은 현재 줌을 사용 (panToWorld와 동일한 방식)
    const panX = -screenX / targetZoom;
    const panY = -screenY / targetZoom;

    console.log(
      `[animatedPanToWorld] worldX=${worldX}, worldZ=${worldZ}, screenX=${screenX}, screenY=${screenY}, panX=${panX}, panY=${panY}, targetZoom=${targetZoom}`
    );

    await this.animateTo(panX, panY, targetZoom, duration);
  }

  /**
   * 셀 크기 기반 줌 레벨 계산 (화면 높이의 특정 비율 차지)
   * @param cellSize 셀 크기
   * @param screenHeightRatio 화면 높이 대비 비율 (0~1)
   */
  calculateZoomForCell(
    cellSize: number,
    screenHeightRatio: number = 0.6
  ): number {
    // 현재 줌 레벨에서 약간 줌 인 (2배)
    // 더 복잡한 계산 대신 간단하게 현재 줌의 절반 사용
    const currentZoom = this.zoom$.value;
    const targetZoom = currentZoom * 0.5; // 2배 줌 인

    console.log(
      `[calculateZoomForCell] cellSize=${cellSize}, currentZoom=${currentZoom}, targetZoom=${targetZoom}`
    );

    // 줌 범위 내로 클램프 (minZoom ~ maxZoom)
    return THREE.MathUtils.clamp(
      targetZoom,
      this.config.minZoom,
      this.config.maxZoom
    );
  }

  /**
   * 내부 애니메이션 함수 (GSAP 사용)
   */
  private animateTo(
    targetX: number,
    targetY: number,
    targetZoom: number,
    duration: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const currentOffset = this.panOffset$.value;
      const currentZoom = this.zoom$.value;

      const animState = {
        x: currentOffset.x,
        y: currentOffset.y,
        zoom: currentZoom,
      };

      // Dynamic import for GSAP to avoid circular dependency issues
      import("gsap").then(({ default: gsap }) => {
        gsap.to(animState, {
          x: targetX,
          y: targetY,
          zoom: targetZoom,
          duration,
          ease: "power2.out",
          onUpdate: () => {
            this.panOffset$.next(
              new THREE.Vector3(animState.x, animState.y, 0)
            );
            this.zoom$.next(animState.zoom);

            if (this.camera instanceof THREE.OrthographicCamera) {
              this.camera.zoom = 1 / animState.zoom;
              this.camera.updateProjectionMatrix();
            }
          },
          onComplete: () => {
            this.emitStateChange();
            resolve();
          },
        });
      });
    });
  }

  /** 정리 */
  dispose(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    this.panOffset$.complete();
    this.zoom$.complete();
    this.edgeZone$.complete();
    this.isDragging$.complete();
    this.lastMousePosition$.complete();
    this.stateChange$.complete();
  }
}
